import { Injectable } from "@angular/core";
import { Store } from "@ngrx/store";
import { combineLatest, Observable, of } from "rxjs";
import { filter, map, switchMap, tap } from "rxjs/operators";

import { AppContextSelector } from "../app-context/app-context.selector";
import { AuthenticationStatus } from "../auth/auth.model";
import { AuthSelector } from "../auth/auth.selector";
import { CustomerManagementAction } from "../customer-management/customer-management.action";
import { AllowedActions, KycState, KycSubStatus } from "../customer-management/customer-management.model";
import { CustomerManagementSelector } from "../customer-management/customer-management.selector";
import { Jurisdiction } from "../jurisdiction/jurisdiction.model";
import { JurisdictionSelector } from "../jurisdiction/jurisdiction.selector";
import { CoreState } from "../state";

import { CustomerComplianceConfig } from "./customer-compliance.config";
import { CurrentAction, CurrentCompliance, CustomerComplianceType } from "./customer-compliance.model";

@Injectable({
	providedIn: "root",
})
export class CustomerComplianceService {
	constructor(
		private store: Store<CoreState>,
		private kycConfig: CustomerComplianceConfig,
		private customerAction: CustomerManagementAction
	) {}

	getCurrentCompliance$(): Observable<CurrentCompliance> {
		return combineLatest([
			this.store.select(AuthSelector.getAuthenticationStatus),
			this.store.select(CustomerManagementSelector.getCustomer),
			this.store.select(JurisdictionSelector.get),
			this.store.select(AppContextSelector.getCountryCode),
		]).pipe(
			filter(
				([auth, customer]) =>
					(auth === AuthenticationStatus.Authenticated && !!customer) ||
					auth === AuthenticationStatus.NotAuthenticated
			),
			map(([auth, customer, jurisdiction, currentCountryCode]) => {
				let type: CustomerComplianceType;
				if (jurisdiction === Jurisdiction.Ukgc || jurisdiction === Jurisdiction.Dga) {
					type = jurisdiction;
				} else {
					const country = customer?.address.country || currentCountryCode;
					type = country ? this.kycConfig.getComplianceByCountryCode(country) || "Generic" : "Generic";
				}
				return {
					type,
					kycState: customer?.kyc.state,
					kycSubStatus: customer?.kyc.subStatus,
					allowedActions: customer?.allowedActions,
					accountInformation: customer?.accountInformation,
					jurisdiction,
					kycLastChangeTimeStamp: customer?.kyc.lastChangeTimestamp,
					lastLogins: customer?.lastLogins,
					playerState: customer?.playerState,
					authenticationStatus: auth,
					rawKycState: customer?.rawKycStatus?.state,
					rawKycSubStatus: customer?.rawKycStatus?.subStatus,
				};
			})
		);
	}

	isDutchKycRequested$(): Observable<boolean> {
		return this.getCurrentCompliance$().pipe(
			map(({ type, kycState }) => this.evaluateIsDutchKycRequested(type, kycState))
		);
	}

	kycStatusCheck$(actionType: AllowedActions): Observable<CurrentAction> {
		return this.getCurrentCompliance$().pipe(
			map((customerKyc) => {
				if (!customerKyc.kycState || customerKyc.type === "Generic") {
					return {
						action: actionType,
						isAllowed: true,
						currentStatus: KycState.Accepted,
						complianceType: customerKyc.type,
					};
				}

				const actionAllowed = customerKyc.allowedActions?.[actionType]?.isAllowed;

				// dutch part is confusing but it's part of requirements
				const kycAllowed = !this.evaluateIsDutchKycRequested(
					customerKyc.type,
					customerKyc.kycState,
					customerKyc.kycSubStatus
				);
				return {
					action: actionType,
					isAllowed: !!(actionAllowed && kycAllowed),
					currentStatus: customerKyc.kycState,
					complianceType: customerKyc.type,
				};
			}),
			switchMap((customerKyc, index) =>
				of(customerKyc).pipe(
					tap(({ isAllowed }) => {
						if (!isAllowed && index < 1) {
							this.store.dispatch(this.customerAction.getCustomer());
						}
					}),
					filter(({ isAllowed }) => isAllowed || index > 0)
				)
			)
		);
	}

	// todo this function needs rework #AI-2641, this is just a quick way to solve the certification requirement
	evaluateIsDutchKycRequested(
		type: CustomerComplianceType,
		kycState?: KycState,
		kycSubStatus?: KycSubStatus | undefined
	) {
		if (
			kycSubStatus?.age === KycState.Accepted &&
			kycSubStatus?.bank === KycState.Requested &&
			kycSubStatus?.name === KycState.Accepted
		) {
			return false;
		}

		return type === "Dutch" && kycState === KycState.Requested;
	}
}
